import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Persistent credibility messaging — vertical stock-ticker style.
// Each line scrolls up like a news ticker, holds for a few seconds,
// then the next line slides up into view. Pure CSS animation, defined
// in index.css under @keyframes topnavCredScroll.
const CRED_BADGES = [
  { icon: '🌍', label: 'Works in 61 countries', tone: '#1e3a8a' },
  { icon: '💱', label: 'Live exchange rates',   tone: '#047857' },
  { icon: '🆓', label: 'Free · no signup',      tone: '#b45309' },
];

/**
 * Sticky top nav with logo + section links + mobile hamburger + a
 * vertical-scrolling credibility ticker in the center.
 *
 * Route-aware: on the homepage we smooth-scroll to the section anchor;
 * on any other route (e.g. /articles/:slug) we navigate to /#sectionId,
 * which loads the homepage and scrolls to the right section via the
 * useEffect in HomePage.
 */
export default function TopNav() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  const links = [
    { id: 'home',     label: 'Calculator' },
    { id: 'faq',      label: 'FAQ' },
    { id: 'articles', label: 'Articles' },
    { id: 'contact',  label: 'Contact' },
  ];
  // Each link's href reflects the destination. On the homepage we use
  // `#id` so the browser shows a clean anchor URL; off-homepage we use
  // `/#id` so right-click "open in new tab" still lands on the right view.
  const linkHref = (id) => (isHome ? `#${id}` : `/#${id}`);

  const handleNavClick = (e, id) => {
    e.preventDefault();
    setOpen(false);
    if (isHome) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      // Navigate to homepage with hash; HomePage's useEffect handles the scroll
      navigate(`/#${id}`);
    }
  };

  return (
    <nav
      className="topnav"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        background: 'rgba(254, 253, 248, 0.92)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        borderBottom: '1.5px solid rgba(26, 26, 46, 0.08)',
      }}
    >
      <div
        style={{
          maxWidth: '80rem',
          margin: '0 auto',
          padding: '0.7rem 1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
          position: 'relative',
        }}
      >
        {/* Vertical-scrolling credibility ticker — stock-market style.
            Each line slides up, holds, next line slides up. Pure CSS
            animation. Container is a neutral rectangle; each item is
            colored by its badge tone. The track has the badges listed
            once + a duplicate of the first at the end so the loop back
            to the start is visually seamless. */}
        <div
          className="topnav-cred"
          aria-label="Calculator highlights"
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: '230px',
            height: '32px',
            overflow: 'hidden',
            background: '#fefdf8',
            border: '1.5px solid rgba(26, 26, 46, 0.18)',
            borderRadius: '6px',
            boxShadow:
              'inset 0 1px 0 rgba(255,255,255,0.7), 0 1px 0 rgba(26,26,46,0.06)',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        >
          <div className="topnav-cred-track">
            {CRED_BADGES.map((b, i) => (
              <div
                key={i}
                className="topnav-cred-item"
                style={{ color: b.tone }}
              >
                <span aria-hidden="true" style={{ fontSize: '0.95rem', lineHeight: 1 }}>{b.icon}</span>
                {b.label}
              </div>
            ))}
            {/* Duplicate the first row so the wrap-around is seamless */}
            <div className="topnav-cred-item" style={{ color: CRED_BADGES[0].tone }}>
              <span aria-hidden="true" style={{ fontSize: '0.95rem', lineHeight: 1 }}>{CRED_BADGES[0].icon}</span>
              {CRED_BADGES[0].label}
            </div>
          </div>
        </div>
        {/* Logo / wordmark */}
        <a
          href={linkHref('home')}
          onClick={(e) => handleNavClick(e, 'home')}
          style={{
            fontFamily: 'Caveat, "Playfair Display", serif',
            fontSize: '1.76rem',
            fontWeight: 700,
            color: '#1a1a2e',
            textDecoration: 'none',
            lineHeight: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            letterSpacing: '-0.01em',
          }}
        >
          <span>🧮</span>
          <span>Etsy Fee Calculator</span>
        </a>

        {/* Desktop links */}
        <ul
          className="topnav-links"
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            gap: '1.4rem',
            alignItems: 'center',
          }}
        >
          {links.map(l => (
            <li key={l.id}>
              <a
                href={linkHref(l.id)}
                onClick={(e) => handleNavClick(e, l.id)}
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: '#1a1a2e',
                  textDecoration: 'none',
                  padding: '0.3rem 0.5rem',
                  borderRadius: '0.3rem',
                  transition: 'all 0.12s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#1e3a8a'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#1a1a2e'}
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        {/* Mobile hamburger */}
        <button
          className="topnav-burger"
          onClick={() => setOpen(o => !o)}
          aria-label="Toggle menu"
          aria-expanded={open}
          style={{
            display: 'none',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '0.4rem',
            fontSize: '1.4rem',
          }}
        >
          {open ? '✕' : '☰'}
        </button>
      </div>

      {/* Mobile dropdown */}
      {open && (
        <div
          className="topnav-mobile"
          style={{
            display: 'none',
            borderTop: '1px solid rgba(26, 26, 46, 0.08)',
            background: '#fefdf8',
            padding: '0.5rem 1rem',
          }}
        >
          {links.map(l => (
            <a
              key={l.id}
              href={linkHref(l.id)}
              onClick={(e) => handleNavClick(e, l.id)}
              style={{
                display: 'block',
                padding: '0.65rem 0.5rem',
                fontFamily: 'Inter, sans-serif',
                fontSize: '1.02rem',
                fontWeight: 600,
                color: '#1a1a2e',
                textDecoration: 'none',
                borderBottom: '1px solid rgba(26, 26, 46, 0.05)',
              }}
            >
              {l.label}
            </a>
          ))}
        </div>
      )}
    </nav>
  );
}
